// ProgressScreen.tsx — the in-app "Your recovery" view, opened from the header
// graph button. A warm, encouraging picture of movement over time (no scores-as-
// judgement, no streaks):
//   1. Recovery pulse — a gentle headline synthesis + how many check-ins so far.
//   2. Your journey — the check-in timeline ("you are here").
//   3. How recovery is changing — the three measures, each with a direction word
//      and a sparkline (fetched from the research backend by the user's own pid).
//   4. Moments of practice — Calm Your Body + Toolkit uses (local counts).
// All visuals are CSS + inline SVG (no images), built from the app's own tokens.
import React from 'react';
import { createPortal } from 'react-dom';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Display, Body, Meta, Card, Btn, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';
import { canSync, fetchMyProgress, openResearchCheckin, openFogsCheck, postResearchConsent, type MyProgressInstrument } from '@/services/sync';
import { researchCheckinDue, nextCheckinOpensAt, describeTimeUntil, recordedWaveCount, lastWaveAt } from '@/services/researchCheckin';
import { logConsent } from '@/services/consent';
import { workingOn, sourceLine, fogsWaveFromMine } from '@/services/fogs';
import { CONSENT_VERSIONS } from '@/config/consent';
import { FOGS_FULL_CHECK } from '@/config/features';
import { ReconsentDialog } from '@/flows/research/ReconsentDialog';

// Friendly, plain-language names for the three instruments (no clinical jargon).
const FRIENDLY: Record<string, string> = {
  'ons4-v1': 'Life and wellbeing',
  'wellbeing-6-v1': 'How you feel',
  'wsas-v1': 'Getting on with life',
};
const ORDER = ['wellbeing-6-v1', 'ons4-v1', 'wsas-v1'];

// User-facing measures only. GAD-7 and PHQ-8 are part of the research battery but are
// deliberately NEVER shown back to the user here: clinical anxiety/depression screeners
// are not appropriate as a self-serve dashboard for a trauma population (and PHQ/GAD need
// clinical context). They stay research-only — collected, never surfaced in "Your recovery".
const USER_FACING = new Set(['wellbeing-6-v1', 'ons4-v1', 'wsas-v1']);

const GREEN = 'var(--green-deep, #167a5a)';
const AMBER = 'var(--amber-deep, #9b6712)';
const LILAC = '#6B5BA8';

// Per-measure colour + icon (decorative; SVG, no images). Keyed by instrument id.
const MEASURE_STYLE: Record<string, { tint: string; ink: string; icon: 'feel' | 'life' | 'wellbeing' }> = {
  'wellbeing-6-v1': { tint: 'rgba(29,158,117,0.14)', ink: GREEN, icon: 'feel' },
  'wsas-v1': { tint: 'rgba(107,91,168,0.16)', ink: LILAC, icon: 'life' },
  'ons4-v1': { tint: 'rgba(201,134,26,0.14)', ink: AMBER, icon: 'wellbeing' },
};

// ── inline SVG icons (decorative) ───────────────────────────────────────────
const svg = (children: React.ReactNode, stroke: string, size = 22): React.ReactElement => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const PulseIcon = ({ stroke = GREEN }: { stroke?: string }) => svg(<path d="M3 12h3l2 5 4-12 2 7h3l2-2" />, stroke, 16);
const LeafIcon = ({ stroke = GREEN, size = 18 }: { stroke?: string; size?: number }) =>
  svg(<><path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 9-4 16-9 16z" /><path d="M9 18c0-4 3-8 8-10" /></>, stroke, size);
const MeasureIcon = ({ kind, stroke }: { kind: 'feel' | 'life' | 'wellbeing'; stroke: string }) => {
  if (kind === 'feel') return svg(<><circle cx="12" cy="12" r="9" /><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" /><path d="M9 9.5h.01M15 9.5h.01" /></>, stroke);
  if (kind === 'life') return svg(<><path d="M5 18h14M12 4v3M6 8l1.5 1.5M18 8l-1.5 1.5M3 14h2M19 14h2" /><path d="M8 18a4 4 0 0 1 8 0" /></>, stroke);
  return svg(<path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 7a3.5 3.5 0 0 1 7 3.5C19 15.5 12 20 12 20z" />, stroke);
};

/** A tiny, dependency-free sparkline. Plots so that "better" is always upward,
 *  using the instrument's own min/max for an honest, stable scale. */
function Sparkline({ values, min, max, invert, stroke = GREEN, w = 150, h = 40 }: { values: number[]; min: number; max: number; invert: boolean; stroke?: string; w?: number; h?: number }) {
  const pad = 4;
  if (!values.length) return null;
  const range = max - min || 1;
  const norm = (v: number) => {
    let t = (v - min) / range;
    if (invert) t = 1 - t;
    return Math.max(0, Math.min(1, t));
  };
  const n = values.length;
  const x = (i: number) => (n === 1 ? w / 2 : pad + (i / (n - 1)) * (w - pad * 2));
  const y = (v: number) => h - pad - norm(v) * (h - pad * 2);
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ display: 'block' }}>
      {n > 1 && <path d={d} fill="none" stroke={stroke} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />}
      {values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={i === n - 1 ? 3.2 : 2} fill={stroke} />)}
    </svg>
  );
}

/** Position 0..1 on the scale, oriented so RIGHT/UP = better. */
function frac(v: number, min: number, max: number, higherIsBetter: boolean, mixed: boolean): number {
  let t = (v - min) / ((max - min) || 1);
  t = Math.max(0, Math.min(1, t));
  if (!higherIsBetter && !mixed) t = 1 - t;
  return t;
}

const composites = (ins: MyProgressInstrument): number[] =>
  ins.waves.map((w) => w.composite).filter((v): v is number => typeof v === 'number');

/** Direction read for one measure: a gentle word + arrow + colour + signed delta. */
function readChange(ins: MyProgressInstrument): { word: string; arrow: 'up' | 'flat' | 'down'; color: string; delta: string | null } {
  const vals = composites(ins);
  if (vals.length < 2 || ins.mixed) {
    return { word: vals.length < 2 ? 'Your starting point' : 'Tracking over time', arrow: 'flat', color: 'var(--ink-soft)', delta: null };
  }
  const now = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const oriented = ins.higherIsBetter ? now - prev : prev - now; // +ve = improvement
  const fNow = frac(now, ins.min, ins.max, ins.higherIsBetter, ins.mixed);
  const fPrev = frac(prev, ins.min, ins.max, ins.higherIsBetter, ins.mixed);
  const d = fNow - fPrev;
  const mag = Math.round(Math.abs(oriented));
  const sign = oriented > 0 ? '+' : oriented < 0 ? '−' : '';
  const delta = Math.abs(d) < 0.03 ? 'No change since last check-in' : `${sign}${mag} since last check-in`;
  if (Math.abs(d) < 0.03) return { word: 'Holding steady', arrow: 'flat', color: AMBER, delta };
  if (d > 0) return { word: 'Improving', arrow: 'up', color: GREEN, delta };
  return { word: 'Harder lately', arrow: 'down', color: AMBER, delta };
}

// One theme line: a measure, a GENTLE plain-language phrase for its direction (no raw
// numbers — this screen is a sensitive report, not a clinical readout), and a small
// trend. The sparkline is the only place trends are shown (the duplicate dropdown is gone).
function ChangeRow({ instrument }: { instrument: MyProgressInstrument }) {
  const vals = composites(instrument);
  if (!vals.length) return null;
  const label = FRIENDLY[instrument.id] || instrument.label;
  const style = MEASURE_STYLE[instrument.id] || { tint: 'rgba(29,158,117,0.14)', ink: GREEN, icon: 'feel' as const };
  let phrase = 'Just getting started';
  let color = AMBER;
  if (vals.length >= 2) {
    const c = readChange(instrument);
    color = c.color;
    phrase = c.arrow === 'up' ? 'Gently lifting' : c.arrow === 'down' ? 'A harder stretch lately' : 'Holding steady';
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 0' }}>
      <div style={{ width: 38, height: 38, borderRadius: 999, background: style.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <MeasureIcon kind={style.icon} stroke={style.ink} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Body size={15} style={{ fontWeight: 600 }}>{label}</Body>
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 600, color }}>{phrase}</div>
      </div>
      {vals.length >= 2 && (
        <div style={{ flexShrink: 0 }}>
          <Sparkline values={vals} min={instrument.min} max={instrument.max} invert={!instrument.higherIsBetter && !instrument.mixed} stroke={style.ink} w={116} h={38} />
        </div>
      )}
    </div>
  );
}

// A sensitively-written synthesis of how recovery is changing across the visible
// measures. Plain language, validating, never alarming — setbacks are framed as a
// normal part of recovery. Thematic, never a score.
function recoveryNarrative(instruments: MyProgressInstrument[], maxCount: number): string {
  if (maxCount <= 1) {
    return 'This is your starting point, so there is nothing to compare yet. You have begun, and that matters. Each time you check in, a gentle picture of how you are changing will grow here, at your own pace.';
  }
  let improved = 0;
  let worse = 0;
  let counted = 0;
  for (const ins of instruments) {
    if (ins.mixed) continue;
    const vals = composites(ins);
    if (vals.length < 2) continue;
    const f = (v: number) => frac(v, ins.min, ins.max, ins.higherIsBetter, ins.mixed);
    const d = f(vals[vals.length - 1]) - f(vals[vals.length - 2]);
    counted++;
    if (d > 0.03) improved++;
    else if (d < -0.03) worse++;
  }
  if (!counted) {
    return 'Your picture is still taking shape. As you complete more check-ins, the themes in how you are feeling and coping will become clearer here.';
  }
  if (improved > worse) {
    return 'Taken together, the overall direction is gently upward. Recovery rarely moves in a straight line, so easier stretches and harder ones are both part of it. What stands out is that you keep showing up, and that is where change comes from.';
  }
  if (worse > improved) {
    return 'This has been a harder stretch, and your check-ins reflect that. It does not undo the work you have done. Recovery moves in waves, and turning up through the harder weeks is its own kind of strength. Be gentle with yourself here.';
  }
  return 'Things are holding fairly steady right now. Steadiness is not standing still; it is often where change settles in before the next step. You are staying with it, and that counts.';
}

/** A clean, single-track check-in timeline: completed check-ins as filled dots on one
 *  centred line, the latest ringed, then a soft open dot for the next one. Captions sit
 *  in their own row so nothing shifts the track (the old per-node layout misaligned). */
function JourneyStepper({ count }: { count: number }) {
  const show = Math.min(3, Math.max(1, count));
  const start = count - show + 1;
  const done = Array.from({ length: show }, (_, i) => start + i);
  const Dot = ({ latest }: { latest: boolean }) => (
    <div
      style={{
        width: latest ? 26 : 18,
        height: latest ? 26 : 18,
        borderRadius: 999,
        flexShrink: 0,
        background: GREEN,
        boxShadow: latest ? '0 0 0 5px rgba(29,158,117,0.16)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
      }}
    >
      {latest && <Icon name="check" size={14} sw={2.6} />}
    </div>
  );
  return (
    <div>
      {/* one centred track — dots and connectors share a baseline, no margin hacks */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {done.map((idx, i) => (
          <React.Fragment key={idx}>
            {i > 0 && <div style={{ flex: 1, height: 3, borderRadius: 999, background: GREEN, opacity: 0.45 }} />}
            <Dot latest={idx === count} />
          </React.Fragment>
        ))}
        <div style={{ flex: 1, height: 3, borderRadius: 999, background: 'var(--hair-strong)' }} />
        {/* upcoming */}
        <div style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, border: '2px dashed var(--hair-strong)' }} />
      </div>

      {/* captions in their own row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 13 }}>
        <div>
          <Body size={14.5} style={{ fontWeight: 600 }}>{count} check-in{count === 1 ? '' : 's'} so far</Body>
          <Meta>You are here</Meta>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Body size={13.5} style={{ fontWeight: 600, color: 'var(--ink-soft)' }}>Next check-in</Body>
          <Meta>Coming up</Meta>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hair)', justifyContent: 'center' }}>
        <LeafIcon size={15} />
        <Meta>Showing up, again and again, is what helps recovery grow.</Meta>
      </div>
    </div>
  );
}

/** "What we're working on together": a very brief nod to the themes from the onboarding questions
 *  and/or the full FOGS check, as chips, plus an offer of the full check if not yet done. Themes
 *  only; no scores, no answers. Survivors only. */
function WorkingOnCard({ onInvite, failed }: { onInvite: () => void; failed?: boolean }) {
  const { state, dismissFogsDone } = useProgress();
  const w = workingOn(state.needsThemes, state.fogsResult);
  const completed = !!state.fogsResult || !!state.fogsDoneLocal;
  // Done locally (native, opened unlinked): the result is unknown here, so don't claim a source.
  const doneLocalOnly = !state.fogsResult && !!state.fogsDoneLocal;
  const openedUnlinked = !!state.fogsOpenedAt && state.fogsOpenedMode === 'unlinked';
  const doneLine = <Meta>Full check done. Your results stay on the page you saw.</Meta>;
  const invite = (
    <>
      <button
        type="button"
        onClick={onInvite}
        style={{ width: '100%', minHeight: 48, display: 'flex', alignItems: 'center', gap: 12, padding: 0, background: 'none', border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }}
      >
        <span style={{ flex: 1 }}>
          <Body size={15} style={{ fontWeight: 600 }}>Waiting to assess your needs</Body>
          <Meta>Based on your past relationship experience. A private 10-minute check.</Meta>
        </span>
        <span style={{ color: GREEN, display: 'inline-flex' }}><Icon name="chevRight" size={18} /></span>
      </button>
      {failed && <Meta>Couldn’t connect just now. Please try again in a moment.</Meta>}
      {openedUnlinked && (
        <button
          type="button"
          onClick={dismissFogsDone}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', padding: 0, background: 'none', border: 0, cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--ink-soft)' }}
        >
          I’ve done it
        </button>
      )}
    </>
  );
  return (
    <section aria-labelledby="working-on-title" style={{ marginBottom: 18 }}>
      <Body size={13} style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-soft)', margin: '6px 0 10px' }}>
        <span id="working-on-title">What we’re working on together</span>
      </Body>
      <Card pad={w.rows.length ? '16px 18px' : 18} radius={16}>
        {w.rows.length > 0 ? (
          <>
            <div role="list" aria-label="Themes we’re working on" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {w.rows.map((r) => (
                <span key={r.key} role="listitem" data-theme-chip style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 500, padding: '6px 12px', borderRadius: 999, background: 'var(--hair)', color: 'var(--ink)' }}>
                  {r.key === 'ex' ? 'Your ex' : r.label}
                </span>
              ))}
            </div>
            {completed ? (
              <div style={{ marginTop: 12 }}>{doneLocalOnly ? doneLine : <Meta>{sourceLine(w)}</Meta>}</div>
            ) : (
              <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 10, marginTop: 12 }}>{invite}</div>
            )}
          </>
        ) : doneLocalOnly ? (
          doneLine
        ) : completed ? (
          <>
            <Body size={14.5} style={{ lineHeight: 1.6 }}>Nothing stood out strongly this time. ReNurture covers all five areas, whenever any of them shows up.</Body>
            <div style={{ marginTop: 8 }}><Meta>{sourceLine({ ...w, source: 'full' })}</Meta></div>
          </>
        ) : (
          invite
        )}
      </Card>
    </section>
  );
}

export function ProgressScreen({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const progress = useProgress();
  const token = canSync(auth.session) ? auth.session.accessToken : null;
  const consented = progress.state.researchConsent === 'granted';
  // The "Take part in our research" CTA opens the full consent page in place; on
  // agreeing it records consent to the app AND mirrors it to the research backend.
  const [showConsent, setShowConsent] = React.useState(false);

  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [fogsFailed, setFogsFailed] = React.useState(false);
  const [instruments, setInstruments] = React.useState<MyProgressInstrument[] | null>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => () => { mounted.current = false; }, []);

  // The recovery view is the user's own outcome tracking — it loads for EVERY
  // signed-in user (fetchMyProgress mints their pid regardless of research consent).
  // Research consent only governs the secondary "gift your data" CTA below.
  const load = React.useCallback((manual = false) => {
    if (!token) { setLoading(false); return; }
    if (manual) setRefreshing(true);
    fetchMyProgress(token)
      .then((res) => {
        if (!mounted.current) return;
        setInstruments(res);
        // Progress is an overlay reachable from any tab, so it applies a returned full-check result itself
        // (TriageHome only does so while the Today tab is mounted). setFogsResult is latest-at guarded.
        const fogs = res ? fogsWaveFromMine(res) : null;
        if (fogs) progress.setFogsResult(fogs.flagged, fogs.at ?? undefined);
      })
      .finally(() => { if (mounted.current) { setLoading(false); setRefreshing(false); } });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    load();
    const sub = CapApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      if (isActive) load();
    });
    // Native: the in-app browser (check-in or full check) was closed with Done/X. Retry once
    // shortly after, in case Done beat the page's keepalive save to the server.
    let retry: ReturnType<typeof setTimeout> | undefined;
    const finished = Capacitor.isNativePlatform()
      ? Browser.addListener('browserFinished', () => { load(); clearTimeout(retry); retry = setTimeout(() => load(), 2500); })
      : null;
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      sub.then((h) => h.remove());
      finished?.then((h) => h.remove()).catch(() => {});
      clearTimeout(retry);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [load]);

  const calm = progress.state.calmSessionsCount ?? 0;
  const tools = progress.state.toolUsesCount ?? 0;
  const moments = calm + tools;

  const ordered = instruments
    ? [...instruments]
        .filter((i) => USER_FACING.has(i.id)) // never surface GAD-7/PHQ-8 to the user
        .sort((a, b) => {
          const ia = ORDER.indexOf(a.id);
          const ib = ORDER.indexOf(b.id);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        })
    : [];

  // Backend-verified completion count → drives both the "due" gate and whether the
  // next check-in is the demographics baseline. Cache it so Today's gate stays in sync.
  // SUBMITTED waves, not plottable ones. composites() drops a wave whose composite
  // could not be scored, which is right for the charts and wrong for the due-gate —
  // writing that lower number here made a submitted wave read as outstanding and
  // brought the prompt back. Both screens now share one derivation.
  // null while the fetch has not landed — unknown, not zero. `maxCount` is the
  // display figure; `recorded` is what the due-gate sees, so a page that has not
  // loaded yet cannot claim a check-in is owed.
  const recorded = recordedWaveCount(instruments);
  const maxCount = recorded ?? 0;
  React.useEffect(() => {
    if (instruments) progress.setCompletedCheckinCount(maxCount, lastWaveAt(instruments));
  }, [instruments, maxCount]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only survivors are part of the research/outcome check-in. Other user types get
  // the full app but are never prompted (and the server gives them no PID).
  const isSurvivor = progress.state.userType === 'survivor';
  const dueCheckin = token && isSurvivor
    ? researchCheckinDue(progress.state, Date.now(), recorded)
    : { due: false, wave: -1, windowDay: 0, isBaseline: false };
  const nextCheckinAt = token && isSurvivor ? nextCheckinOpensAt(progress.state, recorded) : null;

  const onCheckin = () => {
    if (!token || !isSurvivor) return;
    const first = maxCount === 0; // demographics baseline only when nothing recorded yet
    void openResearchCheckin(token, undefined, { firstCheckin: first, consent: consented });
    progress.markResearchCheckinOpened();
  };
  let improved = 0;
  let worse = 0;
  ordered.forEach((ins) => {
    if (ins.mixed) return;
    const vals = composites(ins);
    if (vals.length < 2) return;
    const f = (v: number) => frac(v, ins.min, ins.max, ins.higherIsBetter, ins.mixed);
    const d = f(vals[vals.length - 1]) - f(vals[vals.length - 2]);
    if (d > 0.03) improved++;
    else if (d < -0.03) worse++;
  });
  const heroTitle =
    maxCount <= 1 ? 'This is your starting point'
      : improved > worse ? 'Things are looking steadier'
        : worse > improved ? 'A harder stretch lately'
          : 'You are holding steady';
  const heroSub =
    maxCount <= 1 ? 'Check in again another time to see how things shift. Showing up is the first step.'
      : worse > improved ? "That's part of recovery — and you showed up anyway. That matters."
        : "You're showing up for yourself, and that makes a difference.";

  const close = (
    <Pressable as="button" onClick={onClose} ariaLabel="Close" style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--edge), var(--sh1)' }}>
      <Icon name="chevDown" size={20} />
    </Pressable>
  );

  return (
    <div style={{ padding: 'calc(16px + var(--safe-top)) 18px calc(40px + var(--safe-bottom))', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Display text="Your recovery" size={26} weight={400} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pressable as="button" onClick={() => load(true)} ariaLabel="Refresh" style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--edge), var(--sh1)', opacity: refreshing ? 0.5 : 1 }}>
            <Icon name="refresh" size={18} />
          </Pressable>
          {close}
        </div>
      </div>

      {FOGS_FULL_CHECK && isSurvivor && (
        <WorkingOnCard
          failed={fogsFailed}
          onInvite={() => {
            setFogsFailed(false);
            void Promise.resolve(openFogsCheck('progress', { token, consent: consented }))
              .then((mode) => {
                if (mode === 'failed') setFogsFailed(true);
                else progress.markFogsOpened(mode);
              })
              .catch(() => {});
          }}
        />
      )}

      {loading ? (
        <Card pad={16} radius={16} style={{ marginBottom: 18 }}><Meta>Loading your check-ins…</Meta></Card>
      ) : ordered.length ? (
        <>
          {/* 1. Recovery pulse hero (gradient + SVG, no image) */}
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, padding: 20, marginBottom: 18, background: 'linear-gradient(135deg, rgba(29,158,117,0.14), rgba(29,158,117,0.05))', border: '1px solid rgba(29,158,117,0.18)' }}>
            <div style={{ position: 'absolute', right: -10, top: -6, opacity: 0.16 }} aria-hidden="true"><LeafIcon size={120} /></div>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GREEN, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
                <PulseIcon /> Recovery pulse
              </div>
              <Display text={heroTitle} size={25} lh={1.18} weight={400} style={{ maxWidth: '14em' }} />
              <Body size={14} style={{ fontWeight: 700, color: GREEN, margin: '8px 0 6px' }}>{maxCount} check-in{maxCount === 1 ? '' : 's'} completed</Body>
              <Body size={14.5} style={{ lineHeight: 1.55, color: 'var(--ink)', maxWidth: '22em' }}>{heroSub}</Body>
            </div>
          </div>

          {/* 2. Your journey */}
          <Body size={13} style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-soft)', margin: '6px 0 10px' }}>Your journey</Body>
          <Card pad={18} radius={16} style={{ marginBottom: 18 }}>
            <JourneyStepper count={maxCount} />
          </Card>

          {/* 3. How recovery is changing — a sensitively written thematic report,
              then the gentle per-theme lines (the only place trends are shown). */}
          <Body size={13} style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-soft)', margin: '6px 0 10px' }}>How recovery is changing</Body>
          <Card pad={18} radius={16} style={{ marginBottom: 12 }}>
            <Body size={14.5} style={{ lineHeight: 1.62 }}>{recoveryNarrative(ordered, maxCount)}</Body>
            <div style={{ marginTop: 6 }}>
              {ordered.map((ins) => (
                <div key={ins.id} style={{ borderTop: '1px solid var(--hair)' }}>
                  <ChangeRow instrument={ins} />
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : (
        <Card pad={18} radius={16} style={{ marginBottom: 18 }}>
          <Body size={14.5} style={{ lineHeight: 1.6, marginBottom: token ? 12 : 0 }}>
            {token
              ? 'Your first check-in will start your picture here — a gentle way to see how things shift over time. It takes about 2 minutes.'
              : 'Sign in to see how your check-ins change over time.'}
          </Body>
          {/* The baseline is always available to start (trial users included) — it is
              how the picture begins. Opens the demographics baseline (maxCount === 0). */}
          {token && (
            <Btn full size="lg" onClick={onCheckin} ariaLabel="Set your starting point">Set your starting point</Btn>
          )}
        </Card>
      )}

      {/* Once there ARE check-ins, the next scheduled wave appears only when it is
          actually due (6/12/24 weeks…). Opens the current due check-in. */}
      {token && dueCheckin.due && ordered.length > 0 && (
        <Btn full size="lg" onClick={onCheckin} ariaLabel="Complete your scheduled check-in" style={{ marginBottom: 18 }}>
          Complete scheduled Check-in
        </Btn>
      )}

      {/* No check-in due → tell the user, gently, how long until the next one so the
          schedule feels predictable rather than silent. */}
      {token && !dueCheckin.due && ordered.length > 0 && nextCheckinAt != null && (
        <Card pad={14} radius={16} style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 999, background: 'rgba(29,158,117,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: GREEN }}>
            <Icon name="sparkle" size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <Body size={14.5} style={{ fontWeight: 600 }}>Next check-in in {describeTimeUntil(nextCheckinAt, Date.now())}</Body>
            <Meta>You're all caught up — nothing to do until then.</Meta>
          </div>
        </Card>
      )}

      {/* 4. Moments of practice */}
      <Body size={13} style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-soft)', margin: '6px 0 10px' }}>Moments of practice</Body>
      <Card pad={18} radius={16} style={{ marginBottom: 18 }}>
        {moments ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 999, background: 'rgba(29,158,117,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LeafIcon size={22} />
            </div>
            <div style={{ flex: 1 }}>
              <Body size={15.5} style={{ fontWeight: 600 }}>{moments} {moments === 1 ? 'moment' : 'moments'} of practice</Body>
              <Meta>Well done. Small moments create big change. · Calm Your Body {calm} · Toolkit {tools}</Meta>
            </div>
          </div>
        ) : (
          <Body size={14.5} style={{ lineHeight: 1.6 }}>
            When you use Calm Your Body or a Toolkit tool, those moments will gather here.
          </Body>
        )}
      </Card>

      {/* "Gift your data, to help others" — research is an opt-in SECONDARY use of
          the same check-ins. Shown only when the user has NOT consented; tapping it
          opens the full consent page in place and records consent on agreement. */}
      {!consented && token && isSurvivor && (
        <Card pad={18} radius={16} style={{ marginBottom: 14, background: 'rgba(29,158,117,0.06)', border: '1px solid rgba(29,158,117,0.18)' }}>
          <Body size={15} style={{ fontWeight: 600, marginBottom: 6 }}>Take part in our research</Body>
          <Body size={14} style={{ lineHeight: 1.6, marginBottom: 12, color: 'var(--ink-soft)' }}>
            Your check-ins are always your own. You can also choose to let an anonymised copy be part of
            our research into whether the programme works. It’s completely voluntary, and you can stop
            whenever you like.
          </Body>
          <Btn full size="lg" onClick={() => setShowConsent(true)} ariaLabel="Take part in our research">Take part</Btn>
        </Card>
      )}

      {/* The consent page itself — on agreeing, record consent to the app and mirror
          it to the research backend (best-effort), matching the Settings re-consent.
          Portalled to <body> so its position:fixed overlay escapes this screen's
          `rise` (translateY) wrapper, which would otherwise clip a fixed child. */}
      {showConsent && createPortal(
        <ReconsentDialog
          onClose={() => setShowConsent(false)}
          onDecide={(granted) => {
            if (granted) {
              progress.setResearchConsent('granted', CONSENT_VERSIONS.research);
              void logConsent('research', 'granted', CONSENT_VERSIONS.research);
              const s = auth.session;
              if (canSync(s) && s.accessToken) void postResearchConsent(s.accessToken, true);
            }
            // "Not just now" leaves research off and records nothing.
            setShowConsent(false);
          }}
        />,
        document.body,
      )}

      {/* Dev-only "Do another check-in" — in production the time-based cadence drives
          check-ins (the prompt surfaces on Today when a window opens). This manual
          trigger is kept for development/testing only. */}
      {import.meta.env.DEV && (ordered.length > 0) && token && (
        <Btn full size="lg" onClick={onCheckin} ariaLabel="Do another check-in">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            {svg(<path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 7a3.5 3.5 0 0 1 7 3.5C19 15.5 12 20 12 20z" />, '#fff', 18)}
            Do another check-in
          </span>
        </Btn>
      )}
    </div>
  );
}
